import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useCustomerAnalytics } from "../../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import { selectIsVerifiedOrganizationCustomer } from "../../../../../services/redux/selectors/loyaltyServiceSelectors";
import { setBottomSheetState } from "../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setOfferData } from "../../../../../services/redux/slices/userInfoSlice";
import {
  GraphicTypeEnum,
  OfferCardType,
} from "../../../../../services/redux/types/CartLoyaltyV2Types";
import { ItemOffer } from "../../../../../utils/OfferClass/ItemOffer/ItemOffer";
import Offer from "../../../../../utils/OfferClass/Offer/Offer";
import {
  CUSTOMER_ANALYTICS_EVENT,
  MAIN_OFFER_TYPE,
  OFFER_CATEGORY,
  OfferCardSize,
  PagesEnum,
} from "../../../../../utils/enums";
import { capitalizeFirstLetter } from "../../../../../utils/help/textPreparation/textPreparation";
import { isVisitor } from "../../../../../utils/help/user/user";
import { useAuth } from "../../../../Layout/auth/useAuth";

export const useOfferCard = (props: {
  offer: Offer;
  isActiveOffer: boolean;
  size: OfferCardSize;
  disabled?: boolean;
  onClick?: () => void;
  pointsToUnlock?: number;
}) => {
  const { offer, isActiveOffer, size, disabled, onClick, pointsToUnlock } =
    props;

  const isLocked = !!(pointsToUnlock && pointsToUnlock > 0);

  const { t } = useTranslationI18();
  const { handleModal } = useModalHook();
  const addCustomerAnalytics = useCustomerAnalytics();
  const [notApplicableOffer, setOfferNotApplicable] = useState(false);
  const [unavailableOffer, setUnavailableOffer] = useState(false);
  const [outOfStockOffer, setOutOfStockOffer] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const authApp = useAuth();
  const dispatch = useAppDispatch();

  const isOrganizationCustomer = useAppSelector(
    selectIsVerifiedOrganizationCustomer
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const cartAndLoyaltyState = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );

  const redeemText = t("pointing-system:redeem");
  const removeI18 = t("offers:remove");
  const offerNotApplicable = t("offers:offer-unavailable");
  const outOfStockText = t("offers:out-of-stock");
  const offerUnavailableText = t("receipt:offer-unavailable");

  const state = capitalizeFirstLetter(isActiveOffer ? removeI18 : redeemText);

  const router = useRouter();
  const isOfflineModePage = router.pathname.includes(PagesEnum.OfflineMode);

  const shouldOpenBottomSheet =
    restaurantInfo &&
    !bottomSheetModal.isOpen &&
    (!selectedFranchise || !orderTypeParam);

  const addTwist = offer.twistOfferIsAvailable(
    cartAndLoyaltyState.isTwistOfferAvailable
  );

  const isMultiCard = Array.isArray(offer.uiDetails?.offerCard);
  const offerCards = offer.uiDetails?.offerCard as OfferCardType[];

  const offerCard: OfferCardType | undefined = isMultiCard
    ? offerCards[currentCardIndex]
    : (offer.uiDetails?.offerCard as OfferCardType | undefined);

  const offerTitle = Offer.getLocalizedValue(offerCard?.titleText);
  const offerBgText = Offer.getLocalizedValue(offerCard?.backgroundText);
  const offerDetails = offer?.offerCardDetailsText({
    short: size === OfferCardSize.small,
    index: currentCardIndex,
  });

  const imageUrls =
    offer.offerType !== MAIN_OFFER_TYPE.BOGO &&
    offer.uiDetails?.graphic?.type === GraphicTypeEnum.IMAGE &&
    offer.uiDetails.graphic.value.length < 2
      ? offer.uiDetails?.graphic?.value
      : undefined;

  const graphics = offerCard?.graphics;

  const hasBackgroundText =
    offer.uiDetails?.graphic?.type !== GraphicTypeEnum.IMAGE ||
    offer.offerType === MAIN_OFFER_TYPE.BUNDLE ||
    (offer.offerCategory === OFFER_CATEGORY.BOGO_OFFER && !graphics);

  const hasBottomSection =
    offer.milestonePoints ||
    (offer.uiDetails?.graphic?.type === GraphicTypeEnum.IMAGE &&
      offer.offerType !== MAIN_OFFER_TYPE.BUNDLE);

  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  const handleRedeemOffer = () => {
    if (
      restaurantInfo?.config?.hasOfflineMode &&
      isOfflineModePage &&
      !isLocked
    ) {
      dispatch(setOfferData({ packageOffer: offer, isRedeemed: false }));
      if (isVisitor()) {
        authApp.signIn(undefined, true);
      }
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.OFFLINE_REWARD_OPENED);
      handleModal(true, "OfflineBurningPopup/OfflineBurningPopup", {
        componentProps: { offer },
      });

      return;
    }

    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetState(true));

      return;
    }

    if (notApplicableOffer || unavailableOffer) {
      return;
    }

    if (!isOrganizationCustomer) {
      const currentOffer = isActiveOffer
        ? cartAndLoyaltyState.offersArray.find((o) =>
            o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
          )
        : offer;

      if (offer.isItemFromListOffer() && !isActiveOffer) {
        handleModal(true, "ItemFromList/ItemFromList", {
          componentProps: { offer },
        });
      } else {
        handleModal(true, "RedeemLoyalty/RedeemLoyalty", {
          componentProps: {
            offer: currentOffer,
            isActiveOffer,
            pointsToUnlock,
          },
        });
      }
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();

      return;
    }

    if (isLocked) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.LOCKED_REWARDS_CLICK);
    }

    handleRedeemOffer();
  };

  useEffect(() => {
    const unavailableOfferFlag =
      disabled ||
      !offer.isOfferApplicable({
        itemStatusRedux,
        cart: cartLoyaltyV2.cart,
        timeZone: restaurantInfo?.timeZone!,
        orderType: orderTypeParam,
      });

    const deletedItemOfferFlag = offer.hasDeletedItems();
    setUnavailableOffer(deletedItemOfferFlag);
    setOfferNotApplicable(unavailableOfferFlag);
    if (offer.isItemOffer()) {
      setOutOfStockOffer(
        (offer as ItemOffer).isOfferOutOfStock({
          itemStatusRedux,
          orderType: orderTypeParam,
        })
      );
    }
  }, [itemStatusRedux.outOfStockItemsIds, disabled]);

  const createMarkup = (html: string) => ({ __html: html });

  return {
    // state
    notApplicableOffer,
    unavailableOffer,
    outOfStockOffer,
    currentCardIndex,
    setCurrentCardIndex,
    // derived
    isLocked,
    isMultiCard,
    isMainDark,
    addTwist,
    hasBottomSection,
    hasBackgroundText,
    offerCards,
    offerCard,
    offerTitle,
    offerBgText,
    offerDetails,
    imageUrls,
    graphics,
    state,
    // actions
    handleCardClick,
    createMarkup,
    // i18n
    outOfStockText,
    offerNotApplicable,
    offerUnavailableText,
  };
};
